import { useContext, useEffect } from "react";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { AppContext } from "../App";
import { LegalTexts } from "../components/LegalTexts";
import { t } from "../i18n";
import { PromoBanner } from "../components/PromoBanner";
import PhoneLabel from "../components/PhoneLabel";
import { useWizardNavigation } from "../hooks/useWizardNavigation";
import { TrustMessage } from "../components/TrustMessage";

export const BookingForm = () => {
	const { promos } = useContext(AppContext);
	const { nextPage, previousPage } = useWizardNavigation();

	return (
		<>
			<Header />
			{promos && <PromoBanner />}
			<Content />
			<Footer nextPage={nextPage} previousPage={previousPage} />
		</>
	);
};

const Content = () => {
	const {
		data,
		setData,
		centers,
		setCenters,
		setFilteredCenters,
		setBrands,
		isTransitioning,
		errors,
		flow,
	} = useContext(AppContext);

	useEffect(() => {
		if (flow !== "mapfre" && !data.locator) {
			let apiUrl =
				process.env.REACT_APP_API_URL || "http://localhost:3000";
			let token = process.env.REACT_APP_API_TOKEN;

			let controller = new AbortController();
			let options = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ ...data, token }),
				signal: controller.signal,
			};

			fetch(`${apiUrl}/bookings`, options)
				.then((res) => res.json())
				.then((newData) => {
					setData((prevData) => ({
						...prevData,
						locator: newData.locator,
					}));
				})
				.catch((err) => {
					console.error(err);
				})
				.finally(() => {
					controller.abort();
				});
		}
	}, [data, setData, flow]);

	useEffect(() => {
		let apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

		let controller = new AbortController();
		let options = {
			method: "GET",
			headers: { "Content-Type": "application/json" },
			signal: controller.signal,
		};

		let isMounted = true;

		fetch(`${apiUrl}/query/workcenters`, options)
			.then((res) => res.json())
			.then((newData) => {
				if (isMounted && newData.result === "ok") {
					const workcenters = newData.workcenters.filter(
						(center) => center.active,
					);
					setCenters(workcenters);
					setFilteredCenters(workcenters);
				}
			})
			.catch((err) => {
				if (err.name !== "AbortError") console.error(err);
			});

		if (flow !== "mapfre" && data.locator) {
			fetch(`${apiUrl}/query/brands?locator=${data.locator}`, options)
				.then((res) => res.json())
				.then((newData) => {
					if (isMounted && newData.result === "ok") {
						setBrands(newData.brands);
					}
				})
				.catch((err) => {
					if (err.name !== "AbortError") console.error(err);
				});
		}

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, [data.locator, setBrands, setCenters, setFilteredCenters, flow]);

	return (
		<div className="Wrapper">
			<TrustMessage
				iconName="group"
				text="Nos encargamos de todos los trámites con tu aseguradora."
			/>
			<div
				className={
					"content kensei_03" + (isTransitioning ? " fade-in" : "")
				}
			>
				<div className="content_booking_form">
					<h2 className="booking_form_title">
						<strong>
							{t(
								"Solo necesitamos estos datos para iniciar tu cita",
							)}
						</strong>
					</h2>
					{flow === "mapfre" && (
						<form>
							<label>
								<strong>{t("Nombre y Apellidos")}</strong>
								<input
									type="text"
									defaultValue={data?.userName || ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											userName: e.target.value,
										}))
									}
								/>
								{errors.name && (
									<span className="error">{errors.name}</span>
								)}
							</label>
							<label>
								<strong>{t("Teléfono")}</strong>
								<input
									type="tel"
									defaultValue={data ? data.phoneNumber : ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											phoneNumber: e.target.value,
										}))
									}
								/>
								{errors.phone && (
									<span className="error">
										{errors.phone}
									</span>
								)}
							</label>
							<label>
								<strong>{t("Email")}</strong>
								<input
									type="email"
									defaultValue={data ? data.userEmail : ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											userEmail: e.target.value,
										}))
									}
								/>
								{errors.email && (
									<span className="error">
										{errors.email}
									</span>
								)}
							</label>
							<label>
								<strong>{t("Matrícula")}</strong>
								<input
									type="text"
									defaultValue={data ? data.plateNumber : ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											plateNumber: e.target.value,
										}))
									}
								/>
								{errors.plate && (
									<span className="error">
										{errors.plate}
									</span>
								)}
							</label>
							<label>
								<strong>{t("Taller")}</strong>
								<select
									value={data?.workCenterCode || ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											workCenterCode: e.target.value,
											centerCode: e.target.value,
											centerName:
												centers.find(
													(c) =>
														c.code ===
														e.target.value,
												)?.name || "",
										}))
									}
								>
									<option value="">
										{t("Selecciona un taller")}
									</option>
									{centers &&
										centers.map((center) => (
											<option
												key={center.code}
												value={center.code}
											>
												{center.name}
											</option>
										))}
								</select>
								{errors.centerCode && (
									<span className="error">
										{errors.centerCode}
									</span>
								)}
							</label>
						</form>
					)}
					{flow !== "mapfre" && (
						<form>
							<PhoneLabel />
							<label>
								<strong>{t("Matrícula")}</strong>
								<input
									type="text"
									value={data?.plate || ""}
									onChange={(e) =>
										setData((prev) => ({
											...prev,
											plate: e.target.value,
										}))
									}
								/>
								{errors.plate && (
									<span className="error">
										{errors.plate}
									</span>
								)}
							</label>
							<label className="privacy_policy">
								<div className="privacy_policy_content">
									<input
										type="checkbox"
										checked={
											!!(data && data.privacy_policy)
										}
										onChange={(e) =>
											setData((prev) => ({
												...prev,
												privacy_policy:
													e.target.checked,
											}))
										}
									/>
									<span className="link">
										{t("Acepto la")}{" "}
										<a
											href="https://www.ralarsa.com/es/privacidad/"
											target="_blank"
											rel="noopener noreferrer"
										>
											{t("política de privacidad")}
										</a>
									</span>
								</div>
							</label>
						</form>
					)}
					<LegalTexts />
				</div>
			</div>
		</div>
	);
};
